// useDraftManager — generic draft persistence for the scrap form flows.
//
// Extracted from useBookingForm so the same logic backs both the booking
// and receipt create forms (and any future `draft_type`). Owns:
//   - Auto-save on a 30s interval (server `/drafts` primary; sessionStorage
//     fallback when the server is unreachable so refreshes never lose data).
//   - Auto-resume: on mount, fetches the latest server draft for this type
//     and surfaces it via `pendingDraftPrompt` (the page shows a "Resume?"
//     modal — accept loads it, reject discards it).
//   - Manual save / load / delete / list for the Draft Manager modal.
//
// A single "live" draft is tracked per form via a per-tab enc_id kept in
// sessionStorage; auto-save round-trips that one row. Loading an existing
// draft adopts it as the live row so subsequent edits keep updating it.
// "Save current as draft" creates a NEW row and adopts it as live, which is
// how multiple drafts accumulate for browsing.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { apiClient } from '../api/client';
import { endpoints } from '../api/endpoints';
import type {
  DraftDetail,
  DraftSummary,
  DraftsListResponse,
  DraftType,
} from '../types/api';

const AUTOSAVE_INTERVAL_MS = 30_000;

/** The draft types that have a client form behind them. */
export type ManagedDraftType = Extract<DraftType, 'booking' | 'receipt'>;

export interface UseDraftManagerOptions<T> {
  /** Which `draft_type` this form persists as. */
  draftType: ManagedDraftType;
  /** The current form state — auto-save serialises this. */
  formData: T;
  /** Applies a loaded draft back into the form (e.g. setState / setForm). */
  onLoad: (data: T) => void;
  /** Gate auto-save + auto-resume on meaningful content. Default: always. */
  isPopulated?: (data: T) => boolean;
  /** Map the opaque server JSON back into form state. Default: identity cast. */
  deserialize?: (raw: unknown) => T | null;
  /** Compute the stored title (also used as the modal preview line). */
  getTitle?: (data: T) => string;
  /** When false the hook is dormant (no auto-save, no auto-resume) — e.g.
   *  edit mode where drafts must not be touched. Default: true. */
  enabled?: boolean;
}

export interface UseDraftManagerResult<T> {
  /** All drafts for this type (mine + shared) — populated by `listDrafts`. */
  drafts: DraftSummary[];
  /** True when the form has unsaved content that differs from the last save. */
  isDirty: boolean;
  /** True while a manual save is in flight. */
  isSaving: boolean;
  /** A server draft found on mount, awaiting accept/reject (the resume prompt). */
  pendingDraftPrompt: T | null;
  /** Load the pending draft into the form. */
  acceptDraft: () => void;
  /** Discard the pending draft (deletes it server-side). */
  rejectDraft: () => void;
  /** Fetch and cache all drafts for this type. */
  listDrafts: () => Promise<DraftSummary[]>;
  /** Load a specific draft by enc_id into the form (adopts it as live). */
  loadDraft: (encId: string) => Promise<void>;
  /** Delete a specific draft by enc_id and refresh the cached list. */
  deleteDraft: (encId: string) => Promise<void>;
  /** Save the current form state as a NEW draft (adopts it as live). */
  saveDraft: () => Promise<void>;
  /** Clear the live draft (server + session). Call after a successful submit. */
  clearDraft: () => Promise<void>;
}

// ---- sessionStorage helpers -----------------------------------------------

interface SessionKeys {
  state: string;
  encId: string;
}

function sessionKeysFor(draftType: ManagedDraftType): SessionKeys {
  // Booking keeps its historical keys verbatim so existing tabs/drafts keep
  // working across the refactor.
  return {
    state: `tricivo_${draftType}_draft_session`,
    encId: `tricivo_${draftType}_draft_enc_id`,
  };
}

function readSessionDraft<T>(
  key: string,
  deserialize: (raw: unknown) => T | null,
): T | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    return deserialize(JSON.parse(raw) as unknown);
  } catch {
    return null;
  }
}

function writeSessionDraft<T>(key: string, state: T | null): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    if (state === null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, JSON.stringify(state));
  } catch {
    /* quota / privacy mode — ignore */
  }
}

// ---- server round-trip helpers --------------------------------------------

async function loadLatestServerDraft<T>(
  draftType: ManagedDraftType,
  keys: SessionKeys,
  deserialize: (raw: unknown) => T | null,
): Promise<T | null> {
  try {
    const res = await apiClient.get<DraftsListResponse>(endpoints.drafts.list, {
      params: { draft_type: draftType },
    });
    const own = (res.data?.mine ?? [])[0];
    if (!own) return null;
    sessionStorage.setItem(keys.encId, own.enc_id);
    const detail = await apiClient.get<DraftDetail>(
      endpoints.drafts.detail(own.enc_id),
    );
    return deserialize(detail.data?.data);
  } catch (err) {
    // 404 (older deploys) and any other failure degrade to no server draft.
    if (axios.isAxiosError(err)) return null;
    return null;
  }
}

async function persistServerDraft<T>(
  draftType: ManagedDraftType,
  keys: SessionKeys,
  data: T,
  title: string,
  opts: { forceNew?: boolean } = {},
): Promise<void> {
  if (opts.forceNew) {
    // "Save as a new draft" — drop the live pointer so we POST a fresh row.
    sessionStorage.removeItem(keys.encId);
  }
  const existingEnc = sessionStorage.getItem(keys.encId);
  try {
    if (existingEnc) {
      await apiClient.put(endpoints.drafts.detail(existingEnc), { data, title });
      return;
    }
    const created = await apiClient.post<DraftSummary>(endpoints.drafts.list, {
      draft_type: draftType,
      title,
      data,
      visibility: 'private',
    });
    if (created.data?.enc_id) {
      sessionStorage.setItem(keys.encId, created.data.enc_id);
    }
  } catch {
    // Server unreachable — keep the session-only fallback.
    writeSessionDraft(keys.state, data);
  }
}

async function clearServerDraft(keys: SessionKeys): Promise<void> {
  const enc = sessionStorage.getItem(keys.encId);
  try {
    if (enc) await apiClient.delete(endpoints.drafts.detail(enc));
  } catch {
    /* ignore — best effort */
  } finally {
    sessionStorage.removeItem(keys.encId);
    writeSessionDraft(keys.state, null);
  }
}

// ---- hook ------------------------------------------------------------------

export function useDraftManager<T>(
  options: UseDraftManagerOptions<T>,
): UseDraftManagerResult<T> {
  const {
    draftType,
    formData,
    onLoad,
    isPopulated = () => true,
    deserialize = (raw: unknown) => (raw as T | null) ?? null,
    getTitle,
    enabled = true,
  } = options;

  const keys = useMemo(() => sessionKeysFor(draftType), [draftType]);

  const [drafts, setDrafts] = useState<DraftSummary[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [pendingDraftPrompt, setPendingDraftPrompt] = useState<T | null>(null);
  const [autoLoadDone, setAutoLoadDone] = useState(false);

  // Serialised snapshot of the last persisted/loaded state — drives both the
  // auto-save dedup and `isDirty`. A ref (not state) so the interval reads the
  // freshest value without re-subscribing; `isDirty` is recomputed each render
  // (every keystroke re-renders the host page) so it stays in sync.
  const lastSaved = useRef<string>('');

  const titleFor = useCallback(
    (data: T): string => getTitle?.(data) ?? `${capitalise(draftType)} draft`,
    [getTitle, draftType],
  );

  // isDirty — populated content that differs from the last save/load.
  const isDirty =
    enabled && isPopulated(formData) && JSON.stringify(formData) !== lastSaved.current;

  // Auto-resume — fetch the latest server (or session) draft once on mount.
  useEffect(() => {
    if (!enabled || autoLoadDone) return;
    let cancelled = false;
    void (async () => {
      const fromServer = await loadLatestServerDraft(draftType, keys, deserialize);
      if (cancelled) return;
      if (fromServer && isPopulated(fromServer)) {
        setPendingDraftPrompt(fromServer);
      } else {
        const fromSession = readSessionDraft(keys.state, deserialize);
        if (fromSession && isPopulated(fromSession)) {
          setPendingDraftPrompt(fromSession);
        }
      }
      setAutoLoadDone(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, autoLoadDone]);

  const acceptDraft = useCallback(() => {
    if (pendingDraftPrompt) {
      lastSaved.current = JSON.stringify(pendingDraftPrompt);
      onLoad(pendingDraftPrompt);
    }
    setPendingDraftPrompt(null);
  }, [pendingDraftPrompt, onLoad]);

  const rejectDraft = useCallback(() => {
    setPendingDraftPrompt(null);
    void clearServerDraft(keys);
  }, [keys]);

  // Auto-save — 30s interval, once the resume decision is settled.
  useEffect(() => {
    if (!enabled || !autoLoadDone || pendingDraftPrompt) return;
    const interval = setInterval(() => {
      if (!isPopulated(formData)) return;
      const serialised = JSON.stringify(formData);
      if (serialised === lastSaved.current) return;
      lastSaved.current = serialised;
      writeSessionDraft(keys.state, formData);
      void persistServerDraft(draftType, keys, formData, titleFor(formData));
    }, AUTOSAVE_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [formData, enabled, autoLoadDone, pendingDraftPrompt, draftType, keys, titleFor, isPopulated]);

  const listDrafts = useCallback(async (): Promise<DraftSummary[]> => {
    try {
      const res = await apiClient.get<DraftsListResponse>(endpoints.drafts.list, {
        params: { draft_type: draftType },
      });
      const all = [...(res.data?.mine ?? []), ...(res.data?.shared ?? [])];
      setDrafts(all);
      return all;
    } catch {
      setDrafts([]);
      return [];
    }
  }, [draftType]);

  const loadDraft = useCallback(
    async (encId: string): Promise<void> => {
      const detail = await apiClient.get<DraftDetail>(
        endpoints.drafts.detail(encId),
      );
      const data = deserialize(detail.data?.data);
      if (!data) return;
      // Adopt this draft as the live row so later edits keep updating it.
      sessionStorage.setItem(keys.encId, encId);
      writeSessionDraft(keys.state, data);
      lastSaved.current = JSON.stringify(data);
      onLoad(data);
    },
    [deserialize, keys, onLoad],
  );

  const deleteDraft = useCallback(
    async (encId: string): Promise<void> => {
      await apiClient.delete(endpoints.drafts.detail(encId));
      // If we deleted the live draft, drop the pointer so we don't PUT a ghost.
      if (sessionStorage.getItem(keys.encId) === encId) {
        sessionStorage.removeItem(keys.encId);
        writeSessionDraft(keys.state, null);
        lastSaved.current = '';
      }
      await listDrafts();
    },
    [keys, listDrafts],
  );

  const saveDraft = useCallback(async (): Promise<void> => {
    if (!isPopulated(formData)) return;
    setIsSaving(true);
    try {
      const serialised = JSON.stringify(formData);
      lastSaved.current = serialised;
      writeSessionDraft(keys.state, formData);
      await persistServerDraft(draftType, keys, formData, titleFor(formData), {
        forceNew: true,
      });
      await listDrafts();
    } finally {
      setIsSaving(false);
    }
  }, [formData, isPopulated, keys, draftType, titleFor, listDrafts]);

  const clearDraft = useCallback(async (): Promise<void> => {
    lastSaved.current = '';
    await clearServerDraft(keys);
  }, [keys]);

  return {
    drafts,
    isDirty,
    isSaving,
    pendingDraftPrompt,
    acceptDraft,
    rejectDraft,
    listDrafts,
    loadDraft,
    deleteDraft,
    saveDraft,
    clearDraft,
  };
}

function capitalise(s: string): string {
  return s.length === 0 ? s : s[0]!.toUpperCase() + s.slice(1);
}
